import {
    FocusEvent,
    KeyboardEvent,
    RefObject,
    useCallback,
    useEffect,
    useRef,
} from 'react';

/** A message row. Rendered with `tabIndex={-1}`; the list raises the current one to 0. */
export const MESSAGE_ROW_ATTRIBUTE = 'data-message-row';
const ROW_SELECTOR = `[${MESSAGE_ROW_ATTRIBUTE}]`;

const FOCUSABLE_SELECTOR =
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]';

/**
 * A menu or a picker inside a message keeps its own keyboard handling, so
 * neither its controls nor its keys are the list's to manage. A menu is
 * known by its role; any other such region sets `data-roving-ignore`.
 * `focusgroup="none"` alone does not count: it only takes a region out of
 * the bar's arrow keys, and a control that is always rendered must still
 * leave the Tab order with the rest of a closed message.
 */
const MANAGED_ELSEWHERE_SELECTOR = '[role="menu"], [data-roving-ignore]';

/**
 * A message's action bar: the focusgroup the browser moves along with the
 * arrow keys (Chromium 150 and later).
 */
const ACTION_BAR_SELECTOR = '[focusgroup]:not([focusgroup="none"])';

/**
 * Remembers what a control's `tabindex` was before the list set it to -1,
 * so entering the message gives it back exactly: an empty value means the
 * attribute was absent.
 */
const RESTORE_ATTRIBUTE = 'data-roving-restore';

// The controls the list manages in a row: everything focusable except the
// row itself and what a menu or a picker owns. The action bar's buttons are
// among them; out of the Tab order, they are out of its focusgroup too.
const getRowControls = (row: HTMLElement) =>
    Array.from(row.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (element) => {
            if (element === row) return false;
            const owner = element.closest(MANAGED_ELSEWHERE_SELECTOR);
            return !owner || !row.contains(owner);
        },
    );

const suspend = (row: HTMLElement) => {
    getRowControls(row).forEach((control) => {
        if (control.hasAttribute(RESTORE_ATTRIBUTE)) return;
        const original = control.getAttribute('tabindex');
        // A control that is -1 on its own has nothing to give back.
        if (original === '-1') return;
        control.setAttribute(RESTORE_ATTRIBUTE, original ?? '');
        control.tabIndex = -1;
    });
};

const resume = (row: HTMLElement) => {
    row.querySelectorAll<HTMLElement>(`[${RESTORE_ATTRIBUTE}]`).forEach(
        (control) => {
            const original = control.getAttribute(RESTORE_ATTRIBUTE);
            if (original === '') {
                control.removeAttribute('tabindex');
            } else {
                control.setAttribute('tabindex', original);
            }
            control.removeAttribute(RESTORE_ATTRIBUTE);
        },
    );
};

/**
 * Whether a control is shown: neither it nor a wrapper up to `container` is
 * `display: none`, as the quick reactions are below the `lg` breakpoint.
 */
const isShown = (control: HTMLElement, container: HTMLElement) => {
    for (
        let node: HTMLElement | null = control;
        node && node !== container;
        node = node.parentElement
    ) {
        if (getComputedStyle(node).display === 'none') return false;
    }
    return true;
};

/** Whether any of these nodes is a message row or holds one. */
const holdsRow = (nodes: NodeList) =>
    Array.from(nodes).some(
        (node) =>
            node instanceof Element &&
            (node.matches(ROW_SELECTOR) || node.querySelector(ROW_SELECTOR)),
    );

/** The message row a mutation happened in, if any. */
const rowOf = (record: MutationRecord) => {
    const element =
        record.target instanceof Element
            ? record.target
            : record.target.parentElement;
    return element?.closest<HTMLElement>(ROW_SELECTOR) ?? null;
};

/**
 * Reapplies after a batch of DOM changes. A row added or removed walks the
 * whole list; changes inside rows reapply those rows alone.
 */
const applyMutations = (
    records: MutationRecord[],
    apply: (rowsLeaving?: HTMLElement[]) => void,
) => {
    const rowsChanged = records.some(
        (record) =>
            holdsRow(record.addedNodes) || holdsRow(record.removedNodes),
    );
    if (rowsChanged) {
        apply();
        return;
    }
    const touched = new Set<HTMLElement>();
    records.forEach((record) => {
        const row = rowOf(record);
        if (row) touched.add(row);
    });
    if (touched.size > 0) apply(Array.from(touched));
};

/**
 * The message nearest to a removed one that is still in the list: the next
 * newer one, else the next older one. Messages can go in a batch, for
 * example when the list keeps only the newest ones. So the index of the
 * removed message cannot find its neighbour in the new rows.
 */
const nearestRemainingRow = (
    previousRows: HTMLElement[],
    removedIndex: number,
    rows: HTMLElement[],
) => {
    const remaining = new Set(rows);
    const isRemaining = (row: HTMLElement) => remaining.has(row);
    return (
        previousRows.slice(removedIndex + 1).find(isRemaining) ??
        previousRows.slice(0, removedIndex).reverse().find(isRemaining) ??
        null
    );
};

/**
 * Makes a list of chat messages one Tab stop, the way Slack and Teams do.
 * Only the current message row has `tabIndex` 0. While focus is outside the
 * list that is the newest message, so Tab and Shift+Tab enter the list
 * there and a screen reader announces it (each row is an article named by
 * its sender and time). ArrowUp and PageUp move to the older message,
 * ArrowDown and PageDown to the newer one, Home and End to the oldest
 * loaded and the newest. Enter or Tab on a message enters it: its own
 * controls become tabbable again and focus moves to its action bar; Escape
 * returns to the message. Controls in every other message are kept at
 * `tabIndex` -1, so Tab past the entered message's last control leaves the
 * list.
 *
 * Spread the returned handlers on the container and give it the ref. Rows
 * are `[data-message-row]` elements. A row's action bar is a focusgroup
 * toolbar, which the browser moves along with the arrow keys; its buttons
 * are the row's controls like any other.
 */
const useRovingMessageList = <T extends HTMLElement>(
    containerRef: RefObject<T | null>,
) => {
    const currentRowRef = useRef<HTMLElement | null>(null);
    const currentIndexRef = useRef(-1);
    // The rows in order at the last apply. currentIndexRef counts in them.
    const lastRowsRef = useRef<HTMLElement[]>([]);
    const enteredRef = useRef(false);

    const getRows = useCallback(() => {
        const container = containerRef.current;
        if (!container) return [];
        return Array.from(
            container.querySelectorAll<HTMLElement>(ROW_SELECTOR),
        );
    }, [containerRef]);

    // Applies the current row and the entered state. The key and focus
    // paths pass the row that stops being current; the row that becomes
    // current is always included, so a move touches two rows, not the whole
    // list. The render effect and the mutation observer pass nothing and
    // walk every row, which is where new rows and new controls are caught.
    const apply = useCallback(
        (rowsLeaving?: HTMLElement[]) => {
            const container = containerRef.current;
            if (!container) return;
            const rows = getRows();
            if (rows.length === 0) {
                currentRowRef.current = null;
                lastRowsRef.current = rows;
                return;
            }
            // A focused message that is removed, such as a failed one the user
            // removes, drops focus to the page. Focus goes to the nearest
            // message that is still in the list. If no message is left, focus
            // goes to the newest one.
            const neighbourOfRemoved =
                currentRowRef.current &&
                !currentRowRef.current.isConnected &&
                document.activeElement === document.body
                    ? (nearestRemainingRow(
                          lastRowsRef.current,
                          currentIndexRef.current,
                          rows,
                      ) ?? rows[rows.length - 1])
                    : null;
            if (neighbourOfRemoved) {
                currentRowRef.current = neighbourOfRemoved;
                enteredRef.current = false;
            }
            const focusInside =
                neighbourOfRemoved !== null ||
                (document.activeElement !== document.body &&
                    container.contains(document.activeElement));
            // From outside, the list is entered at the newest message. That also
            // hands the stop to a message that arrived while focus was elsewhere,
            // and never moves it while the user is in the list.
            if (
                !focusInside ||
                !currentRowRef.current ||
                !rows.includes(currentRowRef.current)
            ) {
                currentRowRef.current = rows[rows.length - 1];
                enteredRef.current = false;
            }
            if (!rowsLeaving) {
                // The full walk also numbers the articles for the feed.
                const size = String(rows.length);
                rows.forEach((row, index) => {
                    const position = String(index + 1);
                    if (row.getAttribute('aria-posinset') !== position) {
                        row.setAttribute('aria-posinset', position);
                    }
                    if (row.getAttribute('aria-setsize') !== size) {
                        row.setAttribute('aria-setsize', size);
                    }
                });
            }
            const targets = rowsLeaving
                ? new Set([...rowsLeaving, currentRowRef.current])
                : rows;
            targets.forEach((row) => {
                const isCurrent = row === currentRowRef.current;
                const tabIndex = isCurrent ? 0 : -1;
                if (row.tabIndex !== tabIndex) row.tabIndex = tabIndex;
                if (isCurrent && enteredRef.current) {
                    resume(row);
                } else {
                    suspend(row);
                }
            });
            currentIndexRef.current = rows.indexOf(currentRowRef.current);
            lastRowsRef.current = rows;
            neighbourOfRemoved?.focus();
        },
        [containerRef, getRows],
    );

    const observedRef = useRef<{
        container: HTMLElement;
        observer: MutationObserver;
    } | null>(null);
    // Rows and their controls come and go without a render of the list: a
    // new message, a reaction, an older page. A change that adds or removes
    // a row walks the whole list; one inside a row, such as a reaction chip
    // appearing or a button turning enabled, reapplies that row alone. The
    // container can appear after the first render, behind a loading
    // skeleton, so the observer follows it and walks the list when it
    // attaches. The list writes neither `disabled` nor `href`, so watching
    // them cannot loop.
    useEffect(() => {
        const container = containerRef.current;
        if (observedRef.current?.container === container) return;
        observedRef.current?.observer.disconnect();
        observedRef.current = null;
        if (!container) return;
        apply();
        if (typeof MutationObserver === 'undefined') return;
        const observer = new MutationObserver((records) =>
            applyMutations(records, apply),
        );
        observer.observe(container, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['disabled', 'href'],
        });
        observedRef.current = { container, observer };
    });
    useEffect(
        () => () => {
            observedRef.current?.observer.disconnect();
        },
        [],
    );

    const leaving = (row: HTMLElement | null) => (row ? [row] : []);

    const moveTo = (row: HTMLElement) => {
        const previous = currentRowRef.current;
        currentRowRef.current = row;
        enteredRef.current = false;
        apply(leaving(previous));
        row.focus();
    };

    // Entering a message lands on the first shown button of its action bar,
    // else on the first control the list manages. The list finds the bar by
    // its focusgroup attribute first, because the sender's avatar comes
    // before it.
    const getEntryControl = (row: HTMLElement) => {
        const controls = getRowControls(row);
        const bar = row.querySelector<HTMLElement>(ACTION_BAR_SELECTOR);
        return (
            (bar &&
                controls.find(
                    (control) => bar.contains(control) && isShown(control, bar),
                )) ??
            controls[0] ??
            null
        );
    };

    const enter = (row: HTMLElement) => {
        const previous = currentRowRef.current;
        currentRowRef.current = row;
        enteredRef.current = true;
        apply(leaving(previous));
        const entryControl = getEntryControl(row);
        if (!entryControl) {
            enteredRef.current = false;
            apply([]);
            return false;
        }
        entryControl.focus();
        return true;
    };

    const leave = (row: HTMLElement) => {
        enteredRef.current = false;
        apply([]);
        row.focus();
    };

    const onKeyDown = (event: KeyboardEvent<T>) => {
        if (
            event.defaultPrevented ||
            event.altKey ||
            event.ctrlKey ||
            event.metaKey
        ) {
            return;
        }
        const target = event.target as HTMLElement;
        const row = target.closest<HTMLElement>(ROW_SELECTOR);
        if (!row) return;
        const ownKeysRegion = target.closest(MANAGED_ELSEWHERE_SELECTOR);
        if (ownKeysRegion && row.contains(ownKeysRegion)) return;

        const rows = getRows();
        const index = rows.indexOf(row);
        const onRow = target === row;

        // At the oldest or newest message the key is left to the browser,
        // so ArrowUp on the oldest still scrolls, which loads older ones.
        const moveBy = (step: number) => {
            const next = rows[index + step];
            if (!next) return;
            event.preventDefault();
            moveTo(next);
        };

        switch (event.key) {
            case 'ArrowUp':
            case 'PageUp':
                moveBy(-1);
                break;
            case 'ArrowDown':
            case 'PageDown':
                moveBy(1);
                break;
            case 'Home':
                if (!onRow) return;
                event.preventDefault();
                moveTo(rows[0]);
                break;
            case 'End':
                if (!onRow) return;
                event.preventDefault();
                moveTo(rows[rows.length - 1]);
                break;
            case 'Enter':
            case 'Tab':
                if (!onRow || event.shiftKey) return;
                // A message with nothing to enter lets Tab leave the list.
                if (enter(row)) event.preventDefault();
                break;
            case 'Escape':
                if (onRow) return;
                event.preventDefault();
                leave(row);
                break;
            default:
        }
    };

    // Focus arriving anywhere in a message, by key or by click, makes that
    // message the current one; on one of its controls, the entered one.
    const onFocus = (event: FocusEvent<T>) => {
        const target = event.target as HTMLElement;
        const row = target.closest<HTMLElement>(ROW_SELECTOR);
        if (!row) return;
        const previous = currentRowRef.current;
        currentRowRef.current = row;
        if (target !== row) enteredRef.current = true;
        apply(leaving(previous));
    };

    // Focus leaving the list hands the stop back to the newest message.
    const onBlur = (event: FocusEvent<T>) => {
        const next = event.relatedTarget as Node | null;
        if (next && containerRef.current?.contains(next)) return;
        const previous = currentRowRef.current;
        currentRowRef.current = null;
        enteredRef.current = false;
        apply(leaving(previous));
    };

    return { onKeyDown, onFocus, onBlur };
};

export default useRovingMessageList;
